import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { toast, Bounce } from 'react-toastify';

import { Button } from 'components/Button';
import PageHeader from 'components/PageHeader';
import {
  getIssuancesApi, acceptIssuanceApi, rejectIssuanceApi,
  getSizeConfirmationApi, submitSizeConfirmationApi,
} from 'api/services/uniformManagement';

import { groupIssuances } from './grouping';
import UniformRow from './UniformRow';
import PendingActionCard from './PendingActionCard';
import RejectReasonSheet from './RejectReasonSheet';
import SizeSection from './SizeSection';
import SizeChoiceSheet from './SizeChoiceSheet';
import './myUniforms.css';

/**
 * My Uniforms — the one uniform screen an employee, not an administrator, uses.
 *
 * It answers three questions in the order they matter on a phone: what needs me,
 * what do I hold, and what is finished. Accepting and rejecting happen here
 * rather than on the admin issuance detail page, which is built for someone
 * managing everyone's garments and reads as such on a 390px screen.
 *
 * `PendingAcceptanceModal` still pops app-wide and still owns the nudge. This
 * page is the place an employee can come back to on purpose; the two share the
 * same endpoints and disagree about nothing.
 *
 * Sizes come from a second endpoint because they are a different record. An
 * issuance says what was handed over and never changes; the profile says what
 * the employee asked for and is theirs to maintain. The two differ whenever
 * the warehouse substituted, so the page has to show both — and it is the only
 * place an employee can state a size before the next rotation is ordered.
 */

const PAGE_SIZE = 100;

const toastOptions = { position: 'top-right', autoClose: 2500, transition: Bounce };

const MyUniformsList = () => {
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [failed, setFailed] = useState(false);
  const [actingId, setActingId] = useState(null);
  const [reportTarget, setReportTarget] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [showPast, setShowPast] = useState(false);
  const [sizeRows, setSizeRows] = useState([]);
  const [sizeTarget, setSizeTarget] = useState(null);
  const [savingSize, setSavingSize] = useState(false);

  const load = useCallback((nextPage) => {
    const isFirst = nextPage === 1;
    if (isFirst) { setLoading(true); setFailed(false); } else { setLoadingMore(true); }

    return getIssuancesApi({ page_size: PAGE_SIZE, mine: true, page: nextPage })
      .then(response => {
        const results = response.data?.results || [];
        setRows(prev => (isFirst ? results : [...prev, ...results]));
        setTotal(response.data?.count ?? results.length);
        setPage(nextPage);
      })
      // A failed load used to fall through to the empty state, which told an
      // employee they had been issued nothing when the truth was that the
      // request never came back. Wrong, and unrecoverable without a reload.
      .catch(() => { if (isFirst) setFailed(true); })
      .finally(() => { if (isFirst) setLoading(false); else setLoadingMore(false); });
  }, []);

  const fetchData = useCallback(() => load(1), [load]);

  // Its own request, and a silent failure on purpose: the sizes are the
  // secondary question on this screen, and an error banner over the whole page
  // would hide the garments an employee came here to answer for.
  const loadSizes = useCallback(() => (
    getSizeConfirmationApi()
      .then(response => setSizeRows(response.data?.data || []))
      .catch(() => setSizeRows([]))
  ), []);

  useEffect(() => { fetchData(); loadSizes(); }, [fetchData, loadSizes]);

  // The answer changes one line. Refetching the whole list to learn what we
  // already know costs a round trip and throws away the employee's scroll
  // position mid-task.
  const setRowStatus = (id, status) => {
    setRows(prev => prev.map(row => (row.id === id ? { ...row, status } : row)));
  };

  const handleConfirm = (item) => {
    setActingId(item.id);
    acceptIssuanceApi(item.id)
      .then(() => {
        setRowStatus(item.id, 'ACCEPTED');
        toast.success('Uniform confirmed', toastOptions);
      })
      .catch(e => {
        const message = e.response?.data?.error || e.response?.data?.detail
          || 'Could not confirm this uniform. Please try again.';
        toast.error(message, { ...toastOptions, autoClose: 3000 });
      })
      .finally(() => setActingId(null));
  };

  const handleReport = (payload) => {
    const target = reportTarget;
    setSubmitting(true);
    rejectIssuanceApi(target.id, payload)
      .then(() => {
        setRowStatus(target.id, 'REJECTED');
        setReportTarget(null);
        toast.success('Reported. Your store has been told.', toastOptions);
      })
      .catch(e => {
        const message = e.response?.data?.error || e.response?.data?.detail
          || 'Could not send this report. Please try again.';
        toast.error(message, { ...toastOptions, autoClose: 3000 });
      })
      .finally(() => setSubmitting(false));
  };

  const handleSaveSize = (row, sizeId) => {
    setSavingSize(true);
    submitSizeConfirmationApi({
      selections: [{ uniform_item: row.uniform_item, size: sizeId }],
    })
      .then(() => {
        setSizeTarget(null);
        // Refetched rather than patched in place: saving moves the window
        // state too (it becomes "Confirmed"), and the server owns that
        // derivation. Guessing it here is how the page and the API drift.
        toast.success('Size saved', toastOptions);
        return loadSizes();
      })
      .catch(e => {
        const message = e.response?.data?.error || e.response?.data?.detail
          || 'Could not save your size. Please try again.';
        toast.error(message, { ...toastOptions, autoClose: 4000 });
      })
      .finally(() => setSavingSize(false));
  };

  const today = new Date();
  const { pending, current, past } = useMemo(() => groupIssuances(rows), [rows]);

  const body = () => {
    if (loading) {
      return (
        <div aria-busy="true" aria-label="Loading your uniforms">
          <div className="myu-skeleton" />
          <div className="myu-skeleton" />
          <div className="myu-skeleton" />
        </div>
      );
    }

    if (failed) {
      return (
        <div className="myu-state">
          <i className="fa fa-exclamation-circle" aria-hidden="true" />
          <p>We couldn&apos;t load your uniforms.</p>
          <Button
            type="button"
            className="btn btn-primary btn-sm"
            onClick={fetchData}
            label="Try again"
          />
        </div>
      );
    }

    // Nothing issued yet does not mean nothing to say: someone in a position
    // that uses uniforms can still state the size they want to be given, and
    // an empty state on its own would be a dead end for exactly the people
    // whose first issuance is still ahead of them.
    if (rows.length === 0) {
      return (
        <>
          <div className="myu-state">
            <i className="fa fa-shopping-bag" aria-hidden="true" />
            <p>No uniforms have been issued to you yet.</p>
          </div>
          <SizeSection
            rows={sizeRows}
            onChange={setSizeTarget}
            busyItem={savingSize ? sizeTarget?.uniform_item : null}
          />
        </>
      );
    }

    return (
      <>
        {pending.length > 0 && (
          <section className="myu-section myu-pending" aria-labelledby="myu-pending-title">
            <h4 className="myu-section-title" id="myu-pending-title">
              <i className="fa fa-exclamation-triangle" aria-hidden="true" />
              {pending.length === 1
                ? '1 item needs your confirmation'
                : `${pending.length} items need your confirmation`}
            </h4>
            {pending.map(item => (
              <PendingActionCard
                key={item.id}
                item={item}
                busy={actingId === item.id}
                onConfirm={handleConfirm}
                onReport={setReportTarget}
              />
            ))}
          </section>
        )}

        <SizeSection
          rows={sizeRows}
          onChange={setSizeTarget}
          busyItem={savingSize ? sizeTarget?.uniform_item : null}
        />

        {current.length > 0 && (
          <section className="myu-section" aria-labelledby="myu-current-title">
            <h4 className="myu-section-title" id="myu-current-title">Your uniforms</h4>
            <div className="myu-list myu-list-wide">
              {current.map(item => (
                <UniformRow key={item.id} item={item} today={today} />
              ))}
            </div>
          </section>
        )}

        {past.length > 0 && (
          <section className="myu-section" aria-labelledby="myu-past-title">
            <h4 className="sr-only" id="myu-past-title">Past items</h4>
            <button
              type="button"
              className="myu-past-toggle"
              aria-expanded={showPast}
              onClick={() => setShowPast(open => !open)}
            >
              <span>Past items ({past.length})</span>
              <i className={`fa fa-chevron-${showPast ? 'up' : 'down'}`} aria-hidden="true" />
            </button>
            {showPast && (
              <div className="myu-list myu-list-wide myu-past-list">
                {past.map(item => (
                  <UniformRow key={item.id} item={item} today={today} />
                ))}
              </div>
            )}
          </section>
        )}

        {rows.length < total && (
          <div className="myu-section text-center">
            <Button
              type="button"
              className="btn btn-outline-secondary btn-sm myu-load-more"
              disabled={loadingMore}
              onClick={() => load(page + 1)}
              label={loadingMore ? 'Loading…' : `Load more (${total - rows.length} left)`}
            />
          </div>
        )}
      </>
    );
  };

  // `myu-page`: on a phone this page scrolls INSIDE .content, not the
  // document — the same shell the employee profile uses. With document
  // scrolling, iOS Safari collapses its toolbars as you scroll and the fixed
  // bottom nav does not reliably follow; the profile page never had the
  // problem because its document never moves. See myUniforms.css.
  return (
    <div className="page-wrapper myu-page">
      <div className="content container-fluid">
        <PageHeader title="My Uniforms" />
        <div className="myu">{body()}</div>
      </div>

      <RejectReasonSheet
        open={reportTarget !== null}
        item={reportTarget}
        submitting={submitting}
        onCancel={() => setReportTarget(null)}
        onSubmit={handleReport}
      />

      <SizeChoiceSheet
        open={sizeTarget !== null}
        row={sizeTarget}
        submitting={savingSize}
        onCancel={() => setSizeTarget(null)}
        onSubmit={handleSaveSize}
      />
    </div>
  );
};

export default MyUniformsList;
